// firebase.js — the app's single Firebase instance.
//
// The values below are the PUBLIC Firebase web config. They identify the
// project; they authorise nothing on their own (access is governed by the
// Firebase console's authorised-domain list, the reCAPTCHA check, and
// server-side rules). No secret belongs in this file.
import { initializeApp } from "firebase/app";
import { getAuth, connectAuthEmulator } from "firebase/auth";

const firebaseConfig = {
  apiKey: "AIzaSyCb-GhuJDOu_PR4blKxZtPj14tJzwr7KoI",
  authDomain: "ulearn-abroad.firebaseapp.com",
  projectId: "ulearn-abroad",
  storageBucket: "ulearn-abroad.firebasestorage.app",
  messagingSenderId: "1000288625850",
  appId: "1:1000288625850:web:ede378be1fc34154d78e83",
  measurementId: "G-HCXRZMDTNG",
};

const app = initializeApp(firebaseConfig);
const auth = getAuth(app);

auth.settings = auth.settings || {};

/**
 * Firebase's phone-auth TEST MODE.
 *
 * When `appVerificationDisabledForTesting` is true the SDK stops performing a
 * real reCAPTCHA check, and the server then accepts ONLY the fictional numbers
 * registered under Authentication → Settings → "Phone numbers for testing".
 * A real number sent in this mode is rejected with HTTP 400
 * (INVALID_APP_CREDENTIAL) — which looks exactly like a broken integration.
 *
 * This used to switch on automatically for `NODE_ENV === 'development'`, for
 * hostname localhost/127.0.0.1, OR for ANY host served on port 3001. That last
 * condition is hostname-independent, so the bypass could engage well outside a
 * developer's machine, and on localhost it silently broke sign-in with real
 * numbers.
 *
 * It is now explicit: set REACT_APP_FIREBASE_TEST_MODE=true when you want to
 * work with fictional test numbers. Otherwise every environment, localhost
 * included, uses the real reCAPTCHA + SMS path.
 */
/**
 * Two ways to switch test mode on:
 *   1. REACT_APP_FIREBASE_TEST_MODE=true in the environment (any host), or
 *   2. on localhost ONLY, a runtime flag — run this in the browser console:
 *        localStorage.setItem('ulearn-firebase-test-mode', 'true')
 *      then reload; removeItem + reload switches back to real SMS.
 * The runtime flag exists because dev servers don't always pick up .env
 * changes; it is refused outside localhost so a visitor on the real site can
 * never flip the auth mode.
 */
const isLocalhost =
  typeof window !== "undefined" &&
  ["localhost", "127.0.0.1"].includes(window.location.hostname);
const runtimeTestFlag = (() => {
  try {
    return localStorage.getItem("ulearn-firebase-test-mode") === "true";
  } catch {
    return false;
  }
})();
const TEST_MODE =
  process.env.REACT_APP_FIREBASE_TEST_MODE === "true" ||
  (isLocalhost && runtimeTestFlag);

auth.settings.appVerificationDisabledForTesting = TEST_MODE;

/**
 * Console helper (localhost only): flip test mode WITHOUT a reload.
 *   ulearnTestMode(true)   // fictional console-registered numbers, no SMS
 *   ulearnTestMode(false)  // back to real reCAPTCHA + SMS
 * Takes effect on the next "send code" attempt — the OTP hook builds a fresh
 * verifier per attempt, and the SDK reads this setting at that moment.
 */
if (isLocalhost && typeof window !== "undefined") {
  window.ulearnTestMode = (on = true) => {
    try {
      if (on) localStorage.setItem("ulearn-firebase-test-mode", "true");
      else localStorage.removeItem("ulearn-firebase-test-mode");
    } catch {
      /* private mode — the in-memory switch below still applies */
    }
    auth.settings.appVerificationDisabledForTesting = Boolean(on);
    // eslint-disable-next-line no-console
    console.warn(
      `[firebase] phone-auth TEST MODE is now ${on ? "ON — only fictional console-registered numbers work" : "OFF — real numbers + SMS"}.`,
    );
    return Boolean(on);
  };
}

if (TEST_MODE) {
  // eslint-disable-next-line no-console
  console.warn(
    "[firebase] phone-auth TEST MODE is on: real phone numbers will be " +
      "rejected with a 400. Only numbers registered under Authentication → " +
      "Settings → Phone numbers for testing will work.",
  );
}

if (process.env.REACT_APP_USE_EMULATOR === "true") {
  try {
    connectAuthEmulator(auth, "http://localhost:9099");
  } catch (error) {
    // already connected, or the emulator isn't running — not fatal
    // eslint-disable-next-line no-console
    console.warn("[firebase] auth emulator not connected:", error.message);
  }
}

export { auth, app };
export default app;
